import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';

/**
 * Shared engine behind {@link useTodoChecklist} (shared task) and
 * {@link usePersonalChecklist} (private My-Tasks task). Both checklists have the
 * same query/mutation/sort/invalidate shape; they differ only in their query key,
 * the id field name, the api methods, and which parent query to refresh. This
 * hook is that shape parameterized once — the two public hooks are thin wrappers,
 * so a fix here (invalidation, sort, disabled-key fallback) applies to both.
 */
export function useChecklist<T extends { sortOrder: number }>(params: {
  holderId: string | null;
  /** Parent id (shared itemId or personal personalItemId) — gates enablement. */
  id: string | null;
  enabled: boolean;
  /** Resolved query key (caller supplies the disabled-sentinel fallback). */
  key: QueryKey;
  /** Parent query to refresh after a mutation (list detail / personal board). */
  secondaryKey: (holderId: string) => QueryKey;
  list: (holderId: string, id: string) => Promise<{ checklist: T[] }>;
  create: (holderId: string, id: string, title: string) => Promise<unknown>;
  setDone: (holderId: string, checklistItemId: string, isDone: boolean) => Promise<unknown>;
  remove: (holderId: string, checklistItemId: string) => Promise<unknown>;
  reorder: (holderId: string, id: string, order: string[]) => Promise<unknown>;
}) {
  const queryClient = useQueryClient();
  const { holderId, id, enabled, key } = params;
  const active = !!holderId && !!id;

  const invalidate = () => {
    if (!active) return;
    void queryClient.invalidateQueries({ queryKey: key });
    // Refresh the parent so the "N/M" checklist badge updates.
    void queryClient.invalidateQueries({ queryKey: params.secondaryKey(holderId as string) });
  };

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => params.list(holderId as string, id as string),
    enabled: enabled && active,
  });

  const createItem = useMutation({
    mutationFn: (title: string) => params.create(holderId as string, id as string, title),
    onSuccess: invalidate,
  });
  const setDoneMut = useMutation({
    mutationFn: (vars: { checklistItemId: string; isDone: boolean }) =>
      params.setDone(holderId as string, vars.checklistItemId, vars.isDone),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: (checklistItemId: string) => params.remove(holderId as string, checklistItemId),
    onSuccess: invalidate,
  });
  const reorderMut = useMutation({
    mutationFn: (order: string[]) => params.reorder(holderId as string, id as string, order),
    onSuccess: invalidate,
  });

  const items: T[] = (data?.checklist ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);

  return {
    items,
    loading: isLoading,
    createItem: (title: string) => createItem.mutate(title),
    setDone: (checklistItemId: string, isDone: boolean) =>
      setDoneMut.mutate({ checklistItemId, isDone }),
    deleteItem: (checklistItemId: string) => deleteMut.mutate(checklistItemId),
    reorder: (order: string[]) => reorderMut.mutate(order),
  };
}
